/**
 * ContractorSearchPicker — searchable combobox for contractor/vendor selection.
 *
 * Replaces the non-scalable `<select>` VendorPicker (limit=200) in purchase and
 * sale invoice forms. Uses GET /contractors?q=<search>&limit=30 (pg_trgm on backend)
 * so it scales to 10k+ contractors.
 *
 * Props mirror the old VendorPicker contract so it drops in without changing callers:
 *  value       — currently selected contractor id ('' = none)
 *  onChange    — called with (id, VendorRow | null); null when cleared
 *  selectedLabel — vendor name to display when closed (parent caches this from form data)
 *  disabled    — disables the trigger
 *  invalid     — red border + aria-invalid on trigger
 *  data-testid — forwarded to root div; -search and -option-<id> suffixes added internally
 *  companyId   — explicit `?companyId=` override for the underlying GET /contractors
 *    call (Model B listing, see `resolveContractorListVisibility`). Used during document
 *    CREATE so the vendor picker shows contractors visible from the document's CHOSEN
 *    company (from the document-type picker), not the currently active company switcher
 *    setting nor the "all companies" union — the two can legitimately differ, e.g. in
 *    all-mode there is no active company at all. `undefined`/`null` = unchanged behavior
 *    (no `?companyId=` sent, same union-of-UCA-companies listing as before).
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Loader2, Search, X } from 'lucide-react';

import { api } from '@/lib/api';

export interface VendorRow {
  id: string;
  name: string;
  taxId: string | null;
}

interface ContractorsResponse {
  contractors: VendorRow[];
}

export interface ContractorSearchPickerProps {
  value: string;
  onChange: (id: string, vendor: VendorRow | null) => void;
  /** Vendor name to show in the closed trigger (sourced from form's vendorNameCached). */
  selectedLabel?: string | null;
  disabled?: boolean;
  invalid?: boolean;
  'data-testid'?: string;
  companyId?: string | null;
}

export function ContractorSearchPicker({
  value,
  onChange,
  selectedLabel,
  disabled = false,
  invalid = false,
  'data-testid': testId,
  companyId,
}: ContractorSearchPickerProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery<ContractorsResponse>({
    queryKey: ['contractors-search', search, companyId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '30' });
      if (search) params.set('q', search);
      if (companyId) params.set('companyId', companyId);
      const res = await api.get<ContractorsResponse>(`/contractors?${params.toString()}`);
      return res.data;
    },
    enabled: isOpen,
    staleTime: 15_000,
  });

  const contractors = data?.contractors ?? [];

  function open() {
    if (disabled) return;
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  }

  function close() {
    setIsOpen(false);
    setSearch('');
  }

  function select(row: VendorRow) {
    onChange(row.id, row);
    close();
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange('', null);
  }

  const triggerBase =
    'flex min-h-[38px] w-full cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition bg-bg-primary';

  const triggerState = disabled
    ? 'cursor-not-allowed opacity-50 border-border-primary text-text-muted'
    : invalid
      ? 'border-red-500 ring-1 ring-red-500 text-text-primary hover:border-red-400 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500'
      : 'border-border-primary text-text-primary hover:border-accent-400 focus:outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500';

  const hasValue = !!value;
  // Etykieta zamkniętego pickera: nazwa z cache (selectedLabel); gdy pusta — skrócony
  // id (jak RelationPicker), nie surowy UUID.
  const displayLabel = selectedLabel?.trim() || (value ? value.slice(0, 8) + '…' : '');

  return (
    <div className="relative" data-testid={testId}>
      {/* Trigger */}
      <div
        role="combobox"
        aria-expanded={isOpen}
        aria-invalid={invalid || undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={open}
        onKeyDown={(e) => e.key === 'Enter' && open()}
        className={`${triggerBase} ${triggerState}`}
      >
        <Search className="h-4 w-4 flex-shrink-0 text-text-muted" />
        {hasValue && displayLabel ? (
          <span className="flex-1 truncate">{displayLabel}</span>
        ) : (
          <span className="flex-1 text-text-muted">
            {t('ui.contractorSearchPicker.placeholder')}
          </span>
        )}
        {hasValue && !disabled && (
          <button
            type="button"
            onClick={clear}
            aria-label={t('ui.contractorSearchPicker.clearAriaLabel')}
            className="flex-shrink-0 hover:text-danger-600"
            tabIndex={-1}
            data-testid={testId ? `${testId}-clear` : undefined}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <ChevronDown
          className={`h-4 w-4 flex-shrink-0 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={close} />

          <div className="absolute z-50 mt-1 w-full rounded-lg border border-border-primary bg-bg-primary shadow-lg">
            {/* Search input */}
            <div className="border-b border-border-primary p-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-text-muted" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      close();
                    }
                  }}
                  placeholder={t('ui.contractorSearchPicker.searchPlaceholder')}
                  className="w-full rounded-md border border-border-primary bg-bg-primary py-1.5 pl-8 pr-3 text-sm text-text-primary placeholder-text-muted focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                  data-testid={testId ? `${testId}-search` : undefined}
                />
              </div>
            </div>

            {/* Results list */}
            <div className="max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
                  <span className="ml-2 text-sm text-text-muted">
                    {t('ui.contractorSearchPicker.loading')}
                  </span>
                </div>
              ) : contractors.length === 0 ? (
                <p className="p-3 text-center text-sm text-text-muted">
                  {t('ui.contractorSearchPicker.noResults')}
                </p>
              ) : (
                contractors.map((row) => (
                  <div
                    key={row.id}
                    onClick={() => select(row)}
                    className={[
                      'flex cursor-pointer items-center gap-2 px-3 py-2 text-sm transition',
                      value === row.id
                        ? 'bg-accent-50 text-accent-700'
                        : 'text-text-primary hover:bg-bg-secondary',
                    ].join(' ')}
                    data-testid={testId ? `${testId}-option-${row.id}` : undefined}
                  >
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{row.name}</span>
                      {row.taxId && (
                        <span className="ml-1 text-text-muted">
                          {t('ui.contractorSearchPicker.nipLabel', { taxId: row.taxId })}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
